import type { GameContext } from '../core/context';
import { angleDelta, clamp, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import { Pool } from '../traffic/pool';
import { SpatialHash } from '../traffic/spatialHash';
import { signalColourFor } from '../world/signals';
import type { CityLayout, PedPath, PointOfInterest } from '../world/types';
import type { Vehicle } from '../vehicles/types';
import type { PlayerService } from '../player/playerSystem';
import type { VehicleGrid } from '../traffic/vehicleGrid';
import type { ViewTester } from '../traffic/visibility';
import { busStopSites, idleGroupSpot, type IdleSpot } from './idleSites';
import type { KnockOptions, PedInfo, PedService, PedState, PedStats } from './types';
import { PED_BRUSH_SPEED, PED_HIT_FAST } from '../heat/heatModel';

/**
 * Pedestrian simulation. Pedestrians are not physics bodies: they follow sidewalk paths from the
 * layout, cross at junctions when the lights/traffic allow, panic at danger and tumble when hit.
 * Hit detection uses the shared vehicle grid, so 80 pedestrians cost a few hash lookups, not 80
 * rigid bodies.
 */

export const PED_MAX = 72;
export const PED_POOL = 104;
const SPAWN_MIN_R = 24;
const SPAWN_MAX_R = 78;
const DESPAWN_R = 105;
const WALK_SPEED_MIN = 1.05;
const WALK_SPEED_MAX = 1.65;
const FLEE_SPEED = 4.1;
const CROSS_PATIENCE = 14;
const HIT_MIN_SPEED = 1.8;
const KILL_SPEED = 9;
const NEAR_R = 50;
/** While the focus moves faster than this (m/s), spawns favour the pavements ahead and hidden walkers far behind are recycled. */
const FOCUS_MOVING = 5;
const BEHIND_R = 62;

/**
 * Borough-clock factor on a district's pedestrian density (docs/TRAFFIC.md "Spread"): yards, docks and
 * works empty out after dark, the pubs and clubs of the high street fill up, the terraces go quiet
 * late at night.
 */
export function pedHourFactor(character: readonly string[], hour: number): number {
  const night = hour >= 21 || hour < 5.5;
  const evening = !night && hour >= 18;
  const industrial = character.includes('industrial') || character.includes('docks') || character.includes('railway');
  const nightlife = character.includes('nightlife') || character.includes('pubs');
  if (industrial) return night ? 0.25 : evening ? 0.6 : 1;
  if (nightlife) return night ? 1.15 : 1;
  if (character.includes('residential')) return night ? 0.7 : 1;
  return 1;
}

/** The borough clock as the crowd sees it: the forced / chosen mood's hour, else the clock (cf. audio/ambience.ts audioHour). */
function crowdHour(ctx: GameContext): number {
  const t = ctx.services.tryGet('timeOfDay');
  if (!t) return ctx.state.timeOfDay;
  if (t.forced) return t.forced === 'overcast' ? 13 : t.forced === 'dusk' ? 19.2 : 23.5;
  if (t.setting === 'auto') return t.hour;
  return t.setting === 'day' ? 13 : t.setting === 'dusk' ? 19.2 : 23.5;
}

export type PedLook = { coat: number; trousers: number; skin: number; hat: 0 | 1 | 2; height: number; build: number };

const COATS = [0x6b4a2f, 0x2b3b5c, 0x4b5a3a, 0x7a2d24, 0x8c8c86, 0xb8964a, 0x1f1f24, 0x5c3b4e, 0xc9b79a, 0x36524f];
const TROUSERS = [0x1d1d22, 0x34302a, 0x3d4757, 0x594a37, 0x24303a];
const SKINS = [0xe8c4a0, 0xd9a577, 0xb67b4f, 0x8a5a3b, 0x5e3d28, 0xf0d2b6];

export class Ped {
  id = 0;
  state: PedState = 'walk';
  x = 0;
  z = 0;
  prevX = 0;
  prevZ = 0;
  yaw = 0;
  speed = 0;
  walkSpeed = 1.4;
  // path following
  pathId = 0;
  pt = 0;
  dir: 1 | -1 = 1;
  side = 0;
  // crossing
  crossIdx = -1;
  crossTx = 0;
  crossTz = 0;
  crossPath = -1;
  crossPt = 0;
  waited = 0;
  // timers
  timer = 0;
  panicUntil = 0;
  fleeFromX = 0;
  fleeFromZ = 0;
  // idle groups
  site = -1;
  faceX = 0;
  faceZ = 0;
  leaveAt = 0;
  // tumble
  vx = 0;
  vz = 0;
  vy = 0;
  y = 0;
  lean = 0;
  leanTarget = 0;
  spin = 0;
  dead = false;
  fade = 1;
  hurtRecover = false;
  look: PedLook = { coat: 0, trousers: 0, skin: 0, hat: 0, height: 1, build: 1 };
  phase = 0;
  tickAcc = 0;
  nextCrossCheck = 0;
  bornAt = 0;

  reset(): void {
    this.state = 'walk';
    this.speed = 0;
    this.side = 0;
    this.crossIdx = -1;
    this.waited = 0;
    this.timer = 0;
    this.panicUntil = 0;
    this.site = -1;
    this.vx = 0;
    this.vz = 0;
    this.vy = 0;
    this.y = 0;
    this.lean = 0;
    this.leanTarget = 0;
    this.spin = 0;
    this.dead = false;
    this.fade = 1;
    this.hurtRecover = false;
    this.tickAcc = 0;
    this.nextCrossCheck = 0;
    this.leaveAt = 0;
  }
}

interface CrossingInfo {
  /** Start of the crossing (the path point) */
  sx: number;
  sz: number;
  tx: number;
  tz: number;
  targetPath: number;
  targetPt: number;
  /** Intersection whose signal governs the crossing, and the edges (of the crossed road) that must be red. */
  intersectionId: number | null;
  crossedEdges: number[];
}

interface IdleSite {
  poi: PointOfInterest;
  /** Where the group stands: on the pavement interior / plaza, clear of the kerb (idleSites.ts). */
  spot: IdleSpot;
  size: number;
  members: number;
  cooldownUntil: number;
  /** A bus queue faces this point (the road) instead of the middle of the group. */
  face?: V2;
}

const IDLE_KINDS: Record<string, number> = { pub: 4, club: 3, shop: 2, cafe: 2, bank: 1, phonebox: 1, landmark: 3, garage: 2 };

export class PedSim implements PedService {
  density = 1;
  readonly stats: PedStats = { pedMs: 0, spawned: 0, despawned: 0, hits: 0, poolCreated: 0, poolInUse: 0 };
  readonly list: Ped[] = [];

  private readonly rng: Rng;
  private readonly pool = new Pool<Ped>(() => new Ped(), (p) => p.reset());
  private readonly hash = new SpatialHash<Ped>(8);
  private readonly crossings = new Map<number, CrossingInfo | null>();
  private readonly sites: IdleSite[] = [];
  private focusOverride: V2 | null = null;
  private nextId = 1;
  private step = 0;
  private readonly byId = new Map<number, Ped>();
  /** Every ped-path segment (for spawning near the focus instead of anywhere on the map). */
  private readonly segs: { pathId: number; seg: number; ax: number; az: number; bx: number; bz: number; w: number; d: number }[] = [];
  /** District index -> density x borough-clock factor per metre of pavement (refreshed every few seconds). */
  private readonly districtW: number[] = [];
  private hourAt = -1;
  /** Spawn-target factor for the area around the focus: quiet streets get fewer walkers (1 = full). */
  private localFactor = 1;
  private readonly focusVel: V2 = { x: 0, z: 0 };
  private lastManageFocus: V2 | null = null;
  private lastManageAt = 0;
  /** Segments that touch the spawn ring around `ringAt` (rebuilt when the focus moves). */
  private readonly ring: number[] = [];
  private readonly ringW: number[] = [];
  private ringAt: V2 | null = null;
  private ringInitial = false;

  constructor(
    private readonly ctx: GameContext,
    private readonly layout: CityLayout,
    private readonly grid: VehicleGrid,
    private readonly view: ViewTester,
  ) {
    this.rng = ctx.rng.fork('peds');
    for (let pathId = 0; pathId < layout.pedPaths.length; pathId++) {
      const path = layout.pedPaths[pathId]!;
      const di = layout.districts.findIndex((x) => x.id === path.districtId);
      const n = path.points.length;
      const segCount = path.loop ? n : n - 1;
      for (let i = 0; i < segCount; i++) {
        const a = path.points[i]!;
        const b = path.points[(i + 1) % n]!;
        const sl = Math.hypot(b.x - a.x, b.z - a.z);
        if (sl < 0.5) continue;
        this.segs.push({ pathId, seg: i, ax: a.x, az: a.z, bx: b.x, bz: b.z, w: sl, d: di });
      }
    }
    this.refreshHour();
    for (const poi of layout.pois) {
      const size = IDLE_KINDS[poi.kind];
      if (!size) continue;
      // No pavement or plaza wide enough near the door: no group here rather than one in the road.
      const spot = idleGroupSpot(layout, poi, size);
      if (spot) this.sites.push({ poi, spot, size, members: 0, cooldownUntil: 0 });
    }
    // Bus stops: two or three waiting at the flag, facing the road.
    for (const b of busStopSites(layout)) this.sites.push({ poi: b.poi, spot: b.spot, size: b.size, members: 0, cooldownUntil: 0, face: b.face });
  }

  /** Re-weigh the districts when the borough clock moves on (cheap; every few seconds). */
  private refreshHour(): void {
    const hour = crowdHour(this.ctx);
    if (Math.abs(hour - this.hourAt) < 0.25) return;
    this.hourAt = hour;
    this.layout.districts.forEach((d, i) => {
      this.districtW[i] = (0.1 + d.pedDensity) * pedHourFactor(d.character, hour);
    });
    this.ringAt = null;
  }

  private segW(i: number): number {
    const g = this.segs[i]!;
    return g.w * (g.d >= 0 ? this.districtW[g.d]! : 0.6);
  }

  /** Walkers wanted around the focus right now (the global target, thinned on quiet streets). */
  private get localTarget(): number {
    return Math.round(this.target * this.localFactor);
  }

  // ----------------------------------------------------------------- service surface

  get active(): number {
    return this.list.length;
  }

  get target(): number {
    return this.layout.pedPaths.length === 0 ? 0 : Math.round(PED_MAX * clamp(this.density, 0, 1.5));
  }

  setFocus(pos: V2 | null): void {
    this.focusOverride = pos;
  }

  focus(): V2 {
    if (this.focusOverride) return this.focusOverride;
    const p = this.ctx.services.tryGet('player');
    return p ? p.pos : this.layout.playerSpawn.pos;
  }

  forEachNear(x: number, z: number, r: number, fn: (x: number, z: number, id: number) => void): void {
    this.hash.forEachNear(x, z, r, (p) => fn(p.x, p.z, p.id));
  }

  inspect(): PedInfo[] {
    return this.list.map((p) => ({
      id: p.id,
      x: +p.x.toFixed(2),
      z: +p.z.toFixed(2),
      state: p.state,
      speed: +p.speed.toFixed(2),
      pathId: p.pathId,
      dead: p.dead,
    }));
  }

  get(id: number): Ped | undefined {
    return this.byId.get(id);
  }

  stateOf(id: number): PedState | null {
    return this.byId.get(id)?.state ?? null;
  }

  isDead(id: number): boolean {
    return this.byId.get(id)?.dead ?? false;
  }

  cower(pos: V2, radius: number, seconds: number, chance = 0.6): number {
    const r2 = radius * radius;
    let n = 0;
    for (const q of this.list) {
      if (q.state === 'tumble' || q.state === 'down' || q.state === 'getup' || q.state === 'cower') continue;
      const dx = q.x - pos.x;
      const dz = q.z - pos.z;
      if (dx * dx + dz * dz > r2) continue;
      if (!this.rng.chance(chance)) {
        this.startFlee(q, pos, 4 + this.rng.range(0, 3));
        continue;
      }
      if (q.site >= 0) {
        this.sites[q.site]!.members = Math.max(0, this.sites[q.site]!.members - 1);
        q.site = -1;
      }
      q.state = 'cower';
      q.speed = 0;
      q.timer = seconds * this.rng.range(0.8, 1.25);
      q.fleeFromX = pos.x;
      q.fleeFromZ = pos.z;
      // Turn away from the gun.
      q.yaw = Math.atan2(q.x - pos.x, q.z - pos.z);
      n++;
    }
    return n;
  }

  get poolCreated(): number {
    return this.pool.created;
  }
  get poolInUse(): number {
    return this.pool.inUse;
  }

  // ----------------------------------------------------------------- spawning

  private newPed(x: number, z: number, state: PedState): Ped | null {
    if (this.list.length >= PED_POOL) return null;
    const p = this.pool.acquire();
    p.id = this.nextId++;
    p.x = p.prevX = x;
    p.z = p.prevZ = z;
    p.state = state;
    p.walkSpeed = this.rng.range(WALK_SPEED_MIN, WALK_SPEED_MAX);
    p.side = this.rng.range(-0.7, 0.7);
    p.phase = this.rng.range(0, 6.28);
    p.yaw = this.rng.range(-Math.PI, Math.PI);
    p.bornAt = this.ctx.state.time;
    p.look = {
      coat: this.rng.pick(COATS),
      trousers: this.rng.pick(TROUSERS),
      skin: this.rng.pick(SKINS),
      hat: this.rng.chance(0.28) ? 1 : this.rng.chance(0.12) ? 2 : 0,
      height: this.rng.range(0.92, 1.08),
      build: this.rng.range(0.9, 1.12),
    };
    this.list.push(p);
    this.byId.set(p.id, p);
    this.stats.spawned++;
    return p;
  }

  release(p: Ped): void {
    const i = this.list.indexOf(p);
    if (i >= 0) this.list.splice(i, 1);
    this.byId.delete(p.id);
    if (p.site >= 0) this.sites[p.site]!.members = Math.max(0, this.sites[p.site]!.members - 1);
    this.stats.despawned++;
    this.pool.release(p);
  }

  /** Place a walker on `pathId` at fractional position along the path. */
  private spawnOnPath(pathId: number, segment: number, t: number): Ped | null {
    const path = this.layout.pedPaths[pathId]!;
    const n = path.points.length;
    const a = path.points[segment % n]!;
    const b = path.points[(segment + 1) % n]!;
    const p = this.newPed(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t, 'walk');
    if (!p) return null;
    p.pathId = pathId;
    p.dir = this.rng.chance(0.5) ? 1 : -1;
    p.pt = p.dir === 1 ? (segment + 1) % n : segment % n;
    p.yaw = Math.atan2(p.dir === 1 ? b.x - a.x : a.x - b.x, p.dir === 1 ? b.z - a.z : a.z - b.z);
    return p;
  }

  spawn(x: number, z: number): Ped | null {
    // Nearest path point.
    let best = -1;
    let bestSeg = 0;
    let bestT = 0;
    let bestD = Infinity;
    this.layout.pedPaths.forEach((path, pid) => {
      const n = path.points.length;
      const segs = path.loop ? n : n - 1;
      for (let i = 0; i < segs; i++) {
        const a = path.points[i]!;
        const b = path.points[(i + 1) % n]!;
        const ex = b.x - a.x;
        const ez = b.z - a.z;
        const l2 = ex * ex + ez * ez || 1;
        const t = clamp(((x - a.x) * ex + (z - a.z) * ez) / l2, 0, 1);
        const d = Math.hypot(a.x + ex * t - x, a.z + ez * t - z);
        if (d < bestD) {
          bestD = d;
          best = pid;
          bestSeg = i;
          bestT = t;
        }
      }
    });
    if (best < 0) return null;
    return this.spawnOnPath(best, bestSeg, bestT);
  }

  spawnAt(x: number, z: number): number | null {
    return this.spawn(x, z)?.id ?? null;
  }

  spawnFleeing(pos: V2, from: V2 | null = null): number | null {
    const p = this.spawn(pos.x, pos.z);
    if (!p) return null;
    // Start exactly where the driver got out, even if that is off the pavement.
    p.x = p.prevX = pos.x;
    p.z = p.prevZ = pos.z;
    this.startFlee(p, from ?? { x: pos.x - Math.sin(p.yaw), z: pos.z - Math.cos(p.yaw) }, 6 + this.rng.range(0, 3));
    return p.id;
  }

  /** Segments with some part inside the spawn ring around the focus (cached until the focus moves 12 m). */
  private ringSegments(initial: boolean): void {
    const f = this.focus();
    if (this.ringAt && this.ringInitial === initial && Math.hypot(f.x - this.ringAt.x, f.z - this.ringAt.z) < 12) return;
    this.ringAt = { x: f.x, z: f.z };
    this.ringInitial = initial;
    this.ring.length = 0;
    this.ringW.length = 0;
    const rMin = initial ? 9 : SPAWN_MIN_R;
    const rMax = initial ? SPAWN_MAX_R + 10 : SPAWN_MAX_R;
    let len = 0;
    let wsum = 0;
    for (let i = 0; i < this.segs.length; i++) {
      const g = this.segs[i]!;
      const ex = g.bx - g.ax;
      const ez = g.bz - g.az;
      const l2 = ex * ex + ez * ez || 1;
      const t = clamp(((f.x - g.ax) * ex + (f.z - g.az) * ez) / l2, 0, 1);
      const near = Math.hypot(g.ax + ex * t - f.x, g.az + ez * t - f.z);
      const far = Math.max(Math.hypot(g.ax - f.x, g.az - f.z), Math.hypot(g.bx - f.x, g.bz - f.z));
      if (near > rMax + 12 || far < rMin - 12) continue;
      const w = this.segW(i);
      this.ring.push(i);
      this.ringW.push(w);
      len += g.w;
      wsum += w;
    }
    // Mean weight per metre of pavement around the focus: the high street at night ~1.2, terraces
    // 0.65, the yards in daytime 0.35 and after dark 0.09. The yards get about half the full crowd at
    // night (three in four by day), residential streets and the high street all of it.
    const perM = len > 0 ? wsum / len : 0.6;
    this.localFactor = clamp(0.45 + perM * 0.8, 0.4, 1);
  }

  private pickWalkerSpot(initial: boolean): boolean {
    this.ringSegments(initial);
    if (this.ring.length === 0) return false;
    const g = this.segs[this.rng.weighted(this.ring, this.ringW)]!;
    const pathId = g.pathId;
    const seg = g.seg;
    const t = this.rng.next();
    const x = g.ax + (g.bx - g.ax) * t;
    const z = g.az + (g.bz - g.az) * t;
    const f = this.focus();
    const d = Math.hypot(x - f.x, z - f.z);
    if (initial) {
      if (d < 9 || d > SPAWN_MAX_R + 10) return false;
    } else if (d < SPAWN_MIN_R || d > SPAWN_MAX_R || this.behindMoving(x, z, d, -0.2) || this.view.isVisible(x, z, 2)) {
      return false;
    }
    // Keep a little personal space.
    let crowded = false;
    this.hash.forEachNear(x, z, 1.2, (o) => {
      if (Math.hypot(o.x - x, o.z - z) < 1.1) crowded = true;
    });
    if (crowded) return false;
    return this.spawnOnPath(pathId, seg, t) !== null;
  }

  /** Is (x, z) well behind a moving focus? (`d` = its distance from the focus.) */
  private behindMoving(x: number, z: number, d: number, cos: number): boolean {
    const v = this.focusVel;
    const sp = Math.hypot(v.x, v.z);
    if (sp < FOCUS_MOVING || d < 1) return false;
    return ((x - this.focus().x) * v.x + (z - this.focus().z) * v.z) / (d * sp) < cos && this.rng.chance(0.75);
  }

  /** A free idle site inside the spawn ring (sites are few: a scan is cheap), or -1. */
  private pickSite(initial: boolean): number {
    const f = this.focus();
    const now = this.ctx.state.time;
    let n = 0;
    let pick = -1;
    for (let i = 0; i < this.sites.length; i++) {
      const site = this.sites[i]!;
      if (site.members > 0 || now < site.cooldownUntil) continue;
      const d = Math.hypot(site.spot.x - f.x, site.spot.z - f.z);
      if (initial ? d > SPAWN_MAX_R + 10 || d < 8 : d < SPAWN_MIN_R || d > SPAWN_MAX_R) continue;
      // Reservoir pick: uniform over the free sites in the ring.
      if (this.rng.int(0, n++) === 0) pick = i;
    }
    return pick;
  }

  private trySpawnGroup(initial: boolean): boolean {
    if (this.sites.length === 0) return false;
    const si = this.pickSite(initial);
    if (si < 0) return false;
    const site = this.sites[si]!;
    const now = this.ctx.state.time;
    const poi = site.poi;
    const d = Math.hypot(site.spot.x - this.focus().x, site.spot.z - this.focus().z);
    if (!initial && (this.behindMoving(site.spot.x, site.spot.z, d, -0.2) || this.view.isVisible(site.spot.x, site.spot.z, 4))) return false;
    // Pubs and clubs draw a crowd after dark; a bus queue thins out late.
    const hour = this.hourAt;
    const late = hour >= 21 || hour < 5.5;
    if (late && poi.kind !== 'pub' && poi.kind !== 'club' && this.rng.chance(0.4)) return false;
    // Stand in a loose ring in front of the entrance, on the pavement (the spot is kerb-checked).
    const cx = site.spot.x;
    const cz = site.spot.z;
    let placed = 0;
    for (let k = 0; k < site.size; k++) {
      const ang = (k / site.size) * Math.PI * 2 + this.rng.range(-0.3, 0.3);
      const r = site.spot.ring;
      const p = this.newPed(cx + Math.cos(ang) * r, cz + Math.sin(ang) * r, 'idle');
      if (!p) break;
      p.site = si;
      p.faceX = site.face ? site.face.x : cx;
      p.faceZ = site.face ? site.face.z : cz;
      p.yaw = site.face ? Math.atan2(site.face.x - p.x, site.face.z - p.z) : site.size === 1 ? poi.heading : Math.atan2(cx - p.x, cz - p.z);
      p.leaveAt = now + this.rng.range(25, 70);
      const nearest = this.nearestPath(p.x, p.z);
      p.pathId = nearest.pathId;
      p.pt = nearest.pt;
      site.members++;
      placed++;
    }
    return placed > 0;
  }

  private nearestPath(x: number, z: number): { pathId: number; pt: number } {
    let best = { pathId: 0, pt: 0 };
    let bestD = Infinity;
    this.layout.pedPaths.forEach((path, pid) => {
      path.points.forEach((q, i) => {
        const d = (q.x - x) ** 2 + (q.z - z) ** 2;
        if (d < bestD) {
          bestD = d;
          best = { pathId: pid, pt: i };
        }
      });
    });
    return best;
  }

  /** Focus jumped (teleport / respawn): drop everyone beyond the despawn radius right away. */
  releaseFar(): number {
    const f = this.focus();
    let n = 0;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const p = this.list[i]!;
      if (Math.hypot(p.x - f.x, p.z - f.z) > DESPAWN_R) {
        this.release(p);
        n++;
      }
    }
    return n;
  }

  fillInitial(): void {
    let guard = 0;
    this.refreshHour();
    this.ringSegments(true);
    while (this.list.length < this.localTarget && guard++ < 800) {
      if (this.rng.chance(0.22)) this.trySpawnGroup(true);
      else this.pickWalkerSpot(true);
    }
  }

  // ----------------------------------------------------------------- crossings

  crossingFor(pathId: number, idx: number): CrossingInfo | null {
    const key = pathId * 1024 + idx;
    const cached = this.crossings.get(key);
    if (cached !== undefined) return cached;
    const path = this.layout.pedPaths[pathId]!;
    const s = path.points[idx]!;
    // Candidate: closest point on another path, across a street (4..24 m away).
    let best: { path: number; pt: number; d: number }[] = [];
    this.layout.pedPaths.forEach((other, oid) => {
      if (oid === pathId) return;
      other.points.forEach((q, i) => {
        const d = Math.hypot(q.x - s.x, q.z - s.z);
        if (d >= 4 && d <= 24) best.push({ path: oid, pt: i, d });
      });
    });
    if (best.length === 0) {
      this.crossings.set(key, null);
      return null;
    }
    best.sort((a, b) => a.d - b.d);
    const minD = best[0]!.d;
    best = best.filter((c) => c.d <= minD * 1.3);
    const pick = best[0]!;
    const t = this.layout.pedPaths[pick.path]!.points[pick.pt]!;
    // Governing signal: nearest intersection to the crossing midpoint; crossed road = edges roughly
    // perpendicular to the crossing direction.
    const mx = (s.x + t.x) / 2;
    const mz = (s.z + t.z) / 2;
    let ixId: number | null = null;
    let bestIx = 22;
    for (const ix of this.layout.intersections) {
      const d = Math.hypot(ix.pos.x - mx, ix.pos.z - mz);
      if (d < bestIx) {
        bestIx = d;
        ixId = ix.id;
      }
    }
    const crossed: number[] = [];
    if (ixId !== null) {
      const ix = this.layout.intersections[ixId]!;
      const cl = Math.hypot(t.x - s.x, t.z - s.z) || 1;
      const cx = (t.x - s.x) / cl;
      const cz = (t.z - s.z) / cl;
      for (const eid of ix.edgeIds) {
        const e = this.layout.roads.edges[eid]!;
        const na = this.layout.roads.nodes[e.from]!.pos;
        const nb = this.layout.roads.nodes[e.to]!.pos;
        const el = Math.hypot(nb.x - na.x, nb.z - na.z) || 1;
        const dot = Math.abs(((nb.x - na.x) / el) * cx + ((nb.z - na.z) / el) * cz);
        if (dot < 0.5) crossed.push(eid);
      }
    }
    const info: CrossingInfo = { sx: s.x, sz: s.z, tx: t.x, tz: t.z, targetPath: pick.path, targetPt: pick.pt, intersectionId: ixId, crossedEdges: crossed };
    this.crossings.set(key, info);
    return info;
  }

  private crossingClear(info: CrossingInfo, now: number): boolean {
    if (info.intersectionId !== null && info.crossedEdges.length > 0) {
      const ix = this.layout.intersections[info.intersectionId]!;
      if (ix.signal) {
        // Cross only while every approach on the crossed road is red.
        for (const eid of info.crossedEdges) if (signalColourFor(ix, eid, now) !== 'red') return false;
      }
    }
    const mx = (info.sx + info.tx) / 2;
    const mz = (info.sz + info.tz) / 2;
    const ex = info.tx - info.sx;
    const ez = info.tz - info.sz;
    const l2 = ex * ex + ez * ez || 1;
    let clear = true;
    this.grid.forEachNear(mx, mz, Math.sqrt(l2) / 2 + 22, (v) => {
      if (!clear || v.speed < 1.2) return;
      const t = clamp(((v.pos.x - info.sx) * ex + (v.pos.z - info.sz) * ez) / l2, 0, 1);
      const qx = info.sx + ex * t - v.pos.x;
      const qz = info.sz + ez * t - v.pos.z;
      const d = Math.hypot(qx, qz);
      if (d < 2.4) {
        clear = false;
        return;
      }
      const closing = (v.velocity.x * qx + v.velocity.z * qz) / d;
      if (closing > 0.5 * v.speed && d / Math.max(v.speed, 0.5) < 3.2) clear = false;
    });
    return clear;
  }

  // ----------------------------------------------------------------- update

  fixedUpdate(dt: number): void {
    const now = this.ctx.state.time;
    this.step++;
    const f = this.focus();

    this.hash.clear();
    for (let i = 0; i < this.list.length; i++) {
      const p = this.list[i]!;
      this.hash.insert(p.x, p.z, p);
    }

    for (let i = this.list.length - 1; i >= 0; i--) {
      const p = this.list[i]!;
      p.prevX = p.x;
      p.prevZ = p.z;
      p.tickAcc += dt;
      const dx = p.x - f.x;
      const dz = p.z - f.z;
      const near = dx * dx + dz * dz < NEAR_R * NEAR_R;
      if (p.state === 'tumble' || p.state === 'down' || p.state === 'getup' || p.state === 'cower' || near || (this.step + p.id) % 3 === 0) {
        const adt = p.tickAcc;
        p.tickAcc = 0;
        this.tick(p, adt, now);
      }
    }

    if (this.step % 12 === 0) this.manage(now, f);
  }

  private manage(now: number, f: V2): void {
    if (this.lastManageFocus && now > this.lastManageAt) {
      const k = 1 / (now - this.lastManageAt);
      const vx = (f.x - this.lastManageFocus.x) * k;
      const vz = (f.z - this.lastManageFocus.z) * k;
      if (Math.hypot(vx, vz) < 60) {
        this.focusVel.x += (vx - this.focusVel.x) * 0.5;
        this.focusVel.z += (vz - this.focusVel.z) * 0.5;
      }
    }
    this.lastManageFocus = { x: f.x, z: f.z };
    this.lastManageAt = now;
    if (this.step % 240 === 0) this.refreshHour();
    // The ring (and the local crowd size) follows the focus even while nothing needs spawning.
    this.ringSegments(false);
    const target = this.localTarget;
    const fv = this.focusVel;
    const fSpeed = Math.hypot(fv.x, fv.z);
    for (let i = this.list.length - 1; i >= 0; i--) {
      const p = this.list[i]!;
      const d = Math.hypot(p.x - f.x, p.z - f.z);
      const settled = p.state !== 'tumble' && p.state !== 'down';
      if (p.fade <= 0.02 || d > DESPAWN_R) {
        if (p.fade <= 0.02 || !this.view.isVisible(p.x, p.z, 2)) this.release(p);
      } else if (d > 50 && this.list.length > target + 6 && settled && !this.view.isVisible(p.x, p.z, 2)) {
        this.release(p);
      } else if (fSpeed > FOCUS_MOVING && d > BEHIND_R && settled && ((p.x - f.x) * fv.x + (p.z - f.z) * fv.z) / (d * fSpeed) < -0.5 && !this.view.isVisible(p.x, p.z, 2)) {
        // Well behind a driving player: walk them on to the pavements ahead instead.
        this.release(p);
      }
    }
    // Sites that emptied cool down before another group forms.
    for (const s of this.sites) if (s.members === 0 && s.cooldownUntil === 0) s.cooldownUntil = now + 20;
    if (this.list.length < target) {
      // Thin streets (fast driving, after a long chase) refill several at a time.
      const budget = this.list.length < target * 0.6 ? 4 : this.list.length < target * 0.85 ? 2 : 1;
      let spawned = 0;
      for (let tries = 0; tries < 6 * budget && spawned < budget && this.list.length < target; tries++) {
        const ok = this.rng.chance(0.2) ? this.trySpawnGroup(false) : this.pickWalkerSpot(false);
        if (ok) spawned++;
      }
    }
  }

  // ----------------------------------------------------------------- per-ped behaviour

  private tick(p: Ped, dt: number, now: number): void {
    if (dt <= 0) return;
    switch (p.state) {
      case 'tumble':
        this.tickTumble(p, dt);
        return;
      case 'down':
        p.timer -= dt;
        if (p.dead) {
          if (p.timer < 0) p.fade = Math.max(0, p.fade - dt);
        } else if (p.timer < 0) {
          p.state = 'getup';
          p.timer = 0.9;
          p.leanTarget = 0;
        }
        p.lean += clamp(p.leanTarget - p.lean, -6 * dt, 6 * dt);
        return;
      case 'cower':
        p.timer -= dt;
        p.speed = 0;
        if (p.timer <= 0) this.startFlee(p, { x: p.fleeFromX, z: p.fleeFromZ }, 6);
        else this.checkHit(p);
        return;
      case 'getup':
        p.timer -= dt;
        p.lean += clamp(p.leanTarget - p.lean, -2.2 * dt, 2.2 * dt);
        if (p.timer <= 0) {
          p.lean = 0;
          this.startFlee(p, { x: p.x - Math.sin(p.yaw), z: p.z - Math.cos(p.yaw) }, 7);
        }
        return;
      default:
        break;
    }

    // Danger from vehicles / the player / sirens.
    if (p.state !== 'flee' && this.checkDanger(p)) {
      /* started fleeing */
    }
    if (this.checkHit(p)) return;

    switch (p.state) {
      case 'idle':
        this.tickIdle(p, dt, now);
        break;
      case 'walk':
      case 'flee':
        this.tickWalk(p, dt, now);
        break;
      case 'wait':
        this.tickWait(p, dt, now);
        break;
      case 'cross':
        this.tickCross(p, dt);
        break;
      default:
        break;
    }
  }

  /** checkDanger's grid visitor, kept (with its ped and result in fields) so no closure is built per ped per tick. */
  private dangerPed: Ped | null = null;
  private dangerFound: Vehicle | null = null;
  private readonly dangerVisit = (v: Vehicle): void => {
    if (this.dangerFound) return;
    const p = this.dangerPed!;
    const dx = p.x - v.pos.x;
    const dz = p.z - v.pos.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    const sp = v.speed;
    if (v.siren && sp > 3 && d < 22) {
      this.dangerFound = v;
      return;
    }
    if (sp < 5 || d > 4 + sp * 1.2) return;
    // Dangerous only if it is actually coming at us: ahead of the bumper and inside its swept path.
    const ux = v.velocity.x / sp;
    const uz = v.velocity.z / sp;
    const along = dx * ux + dz * uz; // vehicle -> ped distance along its travel direction
    const lateral = Math.abs(dx * uz - dz * ux);
    if (along > 0 && lateral < v.spec.width / 2 + 1.5) this.dangerFound = v;
  };

  private checkDanger(p: Ped): boolean {
    this.dangerPed = p;
    this.dangerFound = null;
    this.grid.forEachNear(p.x, p.z, 24, this.dangerVisit);
    this.dangerPed = null;
    const dv = this.dangerFound as Vehicle | null;
    this.dangerFound = null;
    if (dv) {
      this.startFlee(p, dv.pos, 3 + this.rng.range(0, 2.5), dv.velocity);
      return true;
    }
    const player = this.ctx.services.tryGet('player') as PlayerService | undefined;
    if (player && player.mode === 'foot' && Math.hypot(player.velocity.x, player.velocity.z) > 4.5) {
      if (Math.hypot(player.pos.x - p.x, player.pos.z - p.z) < 2.6) {
        this.startFlee(p, player.pos, 3);
        return true;
      }
    }
    return false;
  }

  private startFlee(p: Ped, from: V2, seconds: number, vehicleVel?: V2): void {
    if (p.state === 'tumble' || p.state === 'down') return;
    const wasCrossing = p.state === 'cross';
    if (p.site >= 0) {
      this.sites[p.site]!.members = Math.max(0, this.sites[p.site]!.members - 1);
      p.site = -1;
    }
    p.state = 'flee';
    p.panicUntil = this.ctx.state.time + seconds;
    p.fleeFromX = from.x;
    p.fleeFromZ = from.z;
    p.waited = 0;
    if (wasCrossing) {
      // Run for whichever kerb is closer.
      const toTarget = Math.hypot(p.crossTx - p.x, p.crossTz - p.z);
      const info = this.crossingFor(p.pathId, p.crossIdx);
      const toStart = info ? Math.hypot(info.sx - p.x, info.sz - p.z) : Infinity;
      if (toStart < toTarget && info) {
        p.crossTx = info.sx;
        p.crossTz = info.sz;
        p.crossPath = p.pathId;
        p.crossPt = p.crossIdx;
      }
      p.state = 'cross';
      p.panicUntil = this.ctx.state.time + seconds;
      p.speed = Math.max(p.speed, FLEE_SPEED * 0.8);
      return;
    }
    // Dodge sideways of an oncoming vehicle, otherwise choose the path direction leading away.
    this.orientAway(p, vehicleVel);
  }

  /** Pick the path direction that moves away from the threat. */
  private orientAway(p: Ped, vehicleVel?: V2): void {
    const path = this.layout.pedPaths[p.pathId];
    if (!path) return;
    let bestScore = -Infinity;
    let bestDir: 1 | -1 = p.dir;
    for (const dir of [1, -1] as const) {
      // Next point if we went in `dir` from the segment we're on.
      const cur = this.segmentPoints(p, dir);
      if (!cur) continue;
      const mx = cur.x - p.x;
      const mz = cur.z - p.z;
      const ml = Math.hypot(mx, mz) || 1;
      const ax = p.x - p.fleeFromX;
      const az = p.z - p.fleeFromZ;
      const al = Math.hypot(ax, az) || 1;
      let score = (mx * ax + mz * az) / (ml * al);
      if (vehicleVel) {
        const vl = Math.hypot(vehicleVel.x, vehicleVel.z) || 1;
        // Prefer moving across the vehicle's line of travel.
        score += (1 - Math.abs((mx * vehicleVel.x + mz * vehicleVel.z) / (ml * vl))) * 0.6;
      }
      if (score > bestScore) {
        bestScore = score;
        bestDir = dir;
      }
    }
    if (bestDir !== p.dir) {
      // Turn around: the target becomes the point behind us.
      p.pt = this.stepPt(path, p.pt, p.dir === 1 ? -1 : 1);
      p.dir = bestDir;
    }
  }

  /** Target point if walking in `dir` along the segment the ped is currently on. */
  private segmentPoints(p: Ped, dir: 1 | -1): V2 | null {
    const path = this.layout.pedPaths[p.pathId];
    if (!path) return null;
    if (dir === p.dir) return path.points[p.pt] ?? null;
    return path.points[this.stepPt(path, p.pt, p.dir === 1 ? -1 : 1)] ?? null;
  }

  private stepPt(path: PedPath, pt: number, by: number): number {
    const n = path.points.length;
    return path.loop ? (pt + by + n) % n : clamp(pt + by, 0, n - 1);
  }

  private checkHit(p: Ped): boolean {
    let hit: Vehicle | null = null;
    this.grid.forEachNear(p.x, p.z, 4.5, (v) => {
      if (hit || v.speed < HIT_MIN_SPEED) return;
      const ob = v.body;
      const sinA = Math.sin(ob.angle);
      const cosA = Math.cos(ob.angle);
      const dx = p.x - ob.pos.x;
      const dz = p.z - ob.pos.z;
      const lx = dx * cosA - dz * sinA;
      const lz = dx * sinA + dz * cosA;
      if (Math.abs(lx) < v.spec.width / 2 + 0.28 && Math.abs(lz) < v.spec.length / 2 + 0.28) hit = v;
    });
    const hv = hit as Vehicle | null;
    if (!hv) return false;
    this.hitBy(p, hv);
    return true;
  }

  private hitBy(p: Ped, v: Vehicle): void {
    const byPlayer = v.controller === 'player';
    this.knockDown(p.id, v.velocity, v.id, byPlayer);
  }

  knockDown(id: number, velocity: V2, vehicleId: number | null, byPlayer = false, opts?: KnockOptions): boolean {
    const p = this.byId.get(id);
    if (!p) return false;
    if (p.state === 'tumble' || p.state === 'down' || p.state === 'getup') {
      // Only a lethal blow (weapons) lands on somebody who is already down.
      if (!opts?.finish || !opts.lethal || p.dead) return false;
      return this.finishOff(p, velocity, byPlayer, opts);
    }
    const speed = Math.hypot(velocity.x, velocity.z);
    if (p.site >= 0) {
      this.sites[p.site]!.members = Math.max(0, this.sites[p.site]!.members - 1);
      p.site = -1;
    }
    p.state = 'tumble';
    p.vx = velocity.x * 0.62 + this.rng.range(-0.8, 0.8);
    p.vz = velocity.z * 0.62 + this.rng.range(-0.8, 0.8);
    p.vy = 2.4 + Math.min(speed * 0.22, 4.5);
    p.y = 0.05;
    p.spin = this.rng.range(-5, 5) * (0.5 + speed * 0.1);
    p.dead = opts?.lethal ?? speed >= KILL_SPEED;
    // Fall the way we were thrown.
    const forwardDot = p.vx * Math.sin(p.yaw) + p.vz * Math.cos(p.yaw);
    p.leanTarget = (forwardDot >= 0 ? -1 : 1) * (Math.PI / 2 - 0.08);
    p.timer = p.dead ? 12 : this.rng.range(2.6, 4.2);
    p.hurtRecover = !p.dead;
    p.speed = 0;
    this.stats.hits++;
    const pos = { x: p.x, z: p.z };
    this.ctx.events.emit('pedHit', { pedId: p.id, vehicleId, speed, pos });
    if (opts?.crime !== undefined) {
      if (opts.crime) this.ctx.events.emit('crime', { type: opts.crime, pos, severity: opts.severity ?? 3, byPlayer });
    } else {
      // Severity by speed: a brush (< PED_BRUSH_SPEED) is 1, a knock-down in town traffic 2 — both minor
      // offences, accidents rather than crimes (docs/POLICE.md §1) — and a hit at speed 3.
      const severity = p.dead ? 4 : speed > PED_HIT_FAST ? 3 : speed >= PED_BRUSH_SPEED ? 2 : 1;
      this.ctx.events.emit('crime', { type: p.dead ? 'pedKilled' : 'pedHit', pos, severity, byPlayer, ...(vehicleId !== null ? { vehicleId } : {}) });
    }
    // The pedPanic event (handled by the ped system and traffic) scatters witnesses.
    this.ctx.events.emit('pedPanic', { pos, radius: 26, reason: 'hit' });
    return true;
  }

  /** A lethal blow on somebody already down: they stay down for good. */
  private finishOff(p: Ped, velocity: V2, byPlayer: boolean, opts: KnockOptions): boolean {
    p.dead = true;
    p.hurtRecover = false;
    p.timer = 12;
    p.fade = 1;
    if (p.state === 'getup') {
      p.state = 'down';
      p.leanTarget = p.leanTarget || -(Math.PI / 2 - 0.08);
    }
    const pos = { x: p.x, z: p.z };
    this.ctx.events.emit('pedHit', { pedId: p.id, vehicleId: null, speed: Math.hypot(velocity.x, velocity.z), pos });
    if (opts.crime) this.ctx.events.emit('crime', { type: opts.crime, pos, severity: opts.severity ?? 4, byPlayer });
    return true;
  }

  private tickTumble(p: Ped, dt: number): void {
    p.vy -= 18 * dt;
    p.y = Math.max(0, p.y + p.vy * dt);
    const grounded = p.y <= 0.001 && p.vy <= 0;
    if (grounded) {
      p.y = 0;
      p.vy = Math.abs(p.vy) > 2.5 ? -p.vy * 0.28 : 0;
      const sp = Math.hypot(p.vx, p.vz);
      const nsp = Math.max(0, sp - 8.5 * dt);
      const k = sp > 1e-6 ? nsp / sp : 0;
      p.vx *= k;
      p.vz *= k;
      p.spin *= 0.9;
    }
    let nx = p.x + p.vx * dt;
    let nz = p.z + p.vz * dt;
    // Bounce off walls: statics only (cheap, tumbling peds are rare).
    const hits = this.ctx.physics.queryStaticsCircle({ x: nx, z: nz }, 0.3);
    if (hits.length > 0) {
      p.vx = 0;
      p.vz = 0;
      nx = p.x;
      nz = p.z;
    }
    p.x = nx;
    p.z = nz;
    p.yaw += p.spin * dt;
    p.lean += clamp(p.leanTarget - p.lean, -7 * dt, 7 * dt);
    if (grounded && p.vx * p.vx + p.vz * p.vz < 0.04) {
      p.state = 'down';
      p.speed = 0;
    }
  }

  private tickIdle(p: Ped, dt: number, now: number): void {
    // Turn to face the group; occasionally wander off.
    const target = Math.atan2(p.faceX - p.x, p.faceZ - p.z);
    p.yaw += clamp(angleDelta(p.yaw, target), -2 * dt, 2 * dt);
    p.speed = 0;
    p.phase += dt * 0.9;
    if (now > p.leaveAt) {
      if (p.site >= 0) {
        const s = this.sites[p.site]!;
        s.members = Math.max(0, s.members - 1);
        s.cooldownUntil = 0;
        p.site = -1;
      }
      p.state = 'walk';
    }
  }

  private tickWait(p: Ped, dt: number, now: number): void {
    p.waited += dt;
    p.speed = 0;
    const info = this.crossingFor(p.pathId, p.crossIdx);
    if (!info) {
      p.state = 'walk';
      return;
    }
    // Face the road.
    const target = Math.atan2(info.tx - p.x, info.tz - p.z);
    p.yaw += clamp(angleDelta(p.yaw, target), -5 * dt, 5 * dt);
    if (this.crossingClear(info, now)) {
      p.state = 'cross';
      p.crossTx = info.tx + this.rng.range(-0.4, 0.4);
      p.crossTz = info.tz + this.rng.range(-0.4, 0.4);
      p.crossPath = info.targetPath;
      p.crossPt = info.targetPt;
    } else if (p.waited > CROSS_PATIENCE) {
      p.state = 'walk';
      p.crossIdx = -1;
    }
  }

  private tickCross(p: Ped, dt: number): void {
    const now = this.ctx.state.time;
    const panicked = now < p.panicUntil;
    const speed = panicked ? FLEE_SPEED : p.walkSpeed * 1.12;
    const dx = p.crossTx - p.x;
    const dz = p.crossTz - p.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.5) {
      // Arrived: join the target path.
      const path = this.layout.pedPaths[p.crossPath];
      p.crossIdx = -1;
      p.state = panicked ? 'flee' : 'walk';
      if (path) {
        p.pathId = p.crossPath;
        const n = path.points.length;
        p.dir = this.rng.chance(0.5) ? 1 : -1;
        p.pt = path.loop ? (p.crossPt + p.dir + n) % n : clamp(p.crossPt + p.dir, 0, n - 1);
        if (!path.loop && (p.crossPt === 0 || p.crossPt === n - 1)) p.dir = p.crossPt === 0 ? 1 : -1;
        p.side = this.rng.range(-0.7, 0.7);
      }
      return;
    }
    this.moveToward(p, p.crossTx, p.crossTz, speed, dt, 9);
  }

  private tickWalk(p: Ped, dt: number, now: number): void {
    const path = this.layout.pedPaths[p.pathId];
    if (!path) return;
    const fleeing = p.state === 'flee';
    if (fleeing && now >= p.panicUntil) {
      p.state = 'walk';
      return;
    }
    const speed = fleeing ? FLEE_SPEED : p.walkSpeed;
    const n = path.points.length;
    const tp = path.points[p.pt]!;
    // Offset target sideways (keeps peds from marching on one line).
    const prev = path.points[this.stepPt(path, p.pt, -p.dir)]!;
    let sx = tp.x - prev.x;
    let sz = tp.z - prev.z;
    const sl = Math.hypot(sx, sz) || 1;
    sx /= sl;
    sz /= sl;
    const tx = tp.x + sz * p.side;
    const tz = tp.z - sx * p.side;
    const d = Math.hypot(tx - p.x, tz - p.z);
    if (d < 0.7) {
      const arrived = p.pt;
      // Crossing decision at designated points.
      if (!fleeing && path.crossings.includes(arrived) && now >= p.nextCrossCheck) {
        p.nextCrossCheck = now + 6;
        if (this.rng.chance(0.4)) {
          const info = this.crossingFor(p.pathId, arrived);
          if (info) {
            p.crossIdx = arrived;
            p.waited = 0;
            p.state = 'wait';
            return;
          }
        }
      }
      if (path.loop) {
        p.pt = (p.pt + p.dir + n) % n;
      } else {
        const next = p.pt + p.dir;
        if (next < 0 || next >= n) {
          p.dir = (p.dir === 1 ? -1 : 1) as 1 | -1;
          p.pt = clamp(p.pt + p.dir, 0, n - 1);
        } else {
          p.pt = next;
        }
      }
      return;
    }
    this.moveToward(p, tx, tz, speed, dt, 7);
  }

  private moveToward(p: Ped, tx: number, tz: number, speed: number, dt: number, turnRate: number): void {
    const dx = tx - p.x;
    const dz = tz - p.z;
    const want = Math.atan2(dx, dz);
    p.yaw += clamp(angleDelta(p.yaw, want), -turnRate * dt, turnRate * dt);
    // Slow down while turning sharply, speed up smoothly.
    const turnPenalty = clamp(1 - Math.abs(angleDelta(p.yaw, want)) / 2.2, 0.2, 1);
    p.speed += clamp(speed * turnPenalty - p.speed, -9 * dt, 6 * dt);
    p.x += Math.sin(p.yaw) * p.speed * dt;
    p.z += Math.cos(p.yaw) * p.speed * dt;
    p.phase += p.speed * dt * 4.6;
  }

  // ----------------------------------------------------------------- group reactions

  panic(pos: V2, radius: number, except?: Ped): void {
    const r2 = radius * radius;
    for (const q of this.list) {
      if (q === except || q.state === 'tumble' || q.state === 'down' || q.state === 'getup') continue;
      const dx = q.x - pos.x;
      const dz = q.z - pos.z;
      if (dx * dx + dz * dz > r2) continue;
      this.startFlee(q, pos, 3 + this.rng.range(0, 3));
    }
  }

  dispose(): void {
    for (const p of this.list.slice()) this.release(p);
  }
}
